import { CommonModule } from '@angular/common';
import { Component, DestroyRef, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs/operators';

import { PessoaDomain } from '@/core/api/sicom-api/registro/pessoa/pessoa.domain';
import { PrestadorPsmtCertificadoRegularidadeStatus, PrestadorPsmtComposicaoFiltro, PrestadorPsmtMedicoResponse, PrestadorPsmtPrestadorClienteResponse, PrestadorPsmtSituacaoFiltro } from '@/core/api/sicom-api/registro/prestador/prestador-psmt.model';
import { PrestadorPsmtService } from '@/core/api/sicom-api/registro/prestador/prestador-psmt.service';
import { CardContent } from '@/shared/components/card-content/card-content';
import { CustomTableComponent, TableMetadata, TablePerfilValue } from '@/shared/components/layout/custom-table/custom-table.component';
import { FotoMedicoCacheService } from '@/shared/components/foto-diretor/foto-medico-cache.service';
import { DateUtils } from '@/shared/utils/date-utils';
import { PsmtPrestadorFinanceiroNavigationService } from '@/features/registro/prestadores/dashboard-prestadores/components/psmt-prestador/psmt-prestador-financeiro-navigation.service';
import { PrestadorAberturaNavigationService } from '@/features/registro/prestadores/dashboard-prestadores/components/prestador-abertura-navigation.service';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { Select } from 'primeng/select';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
type OpcaoFiltroPsmt = {
    label: string;
    value: PrestadorPsmtSituacaoFiltro;
};

type OpcaoComposicaoPsmt = {
    label: string;
    value: PrestadorPsmtComposicaoFiltro;
};

type PrestadorClienteTabelaRow = {
    id: string;
    uf: string;
    crm: string;
    razaoSocial: string;
    tipoInscricao: string;
    situacao: string;
    statusPsmt: TableTag;
    source: PrestadorPsmtPrestadorClienteResponse;
};

type MedicoPsmtTabelaRow = {
    id: string;
    uf: string;
    crm: string;
    nome: TablePerfilValue;
    situacao: string;
    entrada: string;
    saida: string;
    alertas: TableTag;
    source: PrestadorPsmtMedicoResponse;
};

type TableTag = {
    value: string;
    color?: string;
    textColor?: string;
};

@Component({
    selector: 'app-psmt-prestador',
    standalone: true,
    imports: [DialogoPadraoDirective, CommonModule, FormsModule, ButtonModule, CardContent, CustomTableComponent, DialogModule, PaginatorModule, Select],
    templateUrl: './psmt-prestador.html',
    styleUrl: './psmt-prestador.scss'
})
export class PsmtPrestador {
    pessoa = input<PessoaDomain>();

    readonly carregando = signal(false);
    readonly prestadoresClientes = signal<PrestadorPsmtPrestadorClienteResponse[]>([]);
    readonly prestadoresClientesTabela = computed<PrestadorClienteTabelaRow[]>(() => this.prestadoresClientes().map((prestador) => this.toPrestadorClienteTabelaRow(prestador)));
    readonly totalRecords = signal(0);
    readonly first = signal(0);
    readonly rows = signal(10);
    readonly medicosDialogVisivel = signal(false);
    readonly detalhesDialogVisivel = signal(false);
    readonly medicosCarregando = signal(false);
    readonly prestadorClienteSelecionado = signal<PrestadorPsmtPrestadorClienteResponse | null>(null);
    readonly prestadorClienteDetalhe = signal<PrestadorPsmtPrestadorClienteResponse | null>(null);
    readonly medicos = signal<PrestadorPsmtMedicoResponse[]>([]);
    readonly medicosTabela = computed<MedicoPsmtTabelaRow[]>(() => this.medicos().map((medico) => this.toMedicoTabelaRow(medico)));
    readonly certificadoDetalhe = computed<TableTag>(() => this.certificadoTag(this.prestadorClienteDetalhe()?.certificadoRegularidadeStatus));
    readonly medicosTotalRecords = signal(0);
    readonly medicosFirst = signal(0);
    readonly medicosRows = signal(10);

    filtroPsmt: PrestadorPsmtSituacaoFiltro = 'TODOS';
    readonly filtrosPsmt: OpcaoFiltroPsmt[] = [
        { label: 'Todos', value: 'TODOS' },
        { label: 'Ativos', value: 'ATIVOS' },
        { label: 'Inativos', value: 'INATIVOS' }
    ];
    composicaoPsmt: PrestadorPsmtComposicaoFiltro = 'ATUAL';
    readonly composicoesPsmt: OpcaoComposicaoPsmt[] = [
        { label: 'Composição Atual', value: 'ATUAL' },
        { label: 'Composição Antiga', value: 'ANTIGA' }
    ];
    readonly prestadoresClientesColumnOrder = ['uf', 'crm', 'razaoSocial', 'tipoInscricao', 'situacao', 'statusPsmt'];
    readonly prestadoresClientesMetadata: TableMetadata = {
        headers: {
            uf: { type: 'text', default: '-', hidden: false, value: 'UF' },
            crm: { type: 'text', default: '-', hidden: false, value: 'CRM' },
            razaoSocial: { type: 'text', default: '-', hidden: false, value: 'Razão Social' },
            tipoInscricao: { type: 'text', default: '-', hidden: false, value: 'Tipo Inscrição' },
            situacao: { type: 'text', default: '-', hidden: false, value: 'Situação' },
            statusPsmt: { type: 'tag', default: '-', hidden: false, value: 'Status PSMT' }
        },
        title: 'razaoSocial',
        actions: {
            visualizarMedicos: {
                label: 'Visualizar Médicos',
                icon: 'pi pi-users',
                color: 'info',
                command: (item: PrestadorClienteTabelaRow) => this.abrirMedicos(item.source)
            },
            selecionar: {
                label: 'Selecionar',
                icon: 'pi pi-eye',
                color: 'secondary',
                command: (item: PrestadorClienteTabelaRow) => this.abrirDetalhes(item.source)
            },
            informacoesFinanceiras: {
                label: 'Informações Financeiras',
                icon: 'pi pi-dollar',
                color: 'success',
                command: (item: PrestadorClienteTabelaRow) => this.abrirInformacoesFinanceirasPrestador(item.source)
            },
            abrirPrestador: {
                label: 'Abrir prestador',
                icon: 'pi pi-external-link',
                color: 'primary',
                command: (item: PrestadorClienteTabelaRow) => this.abrirPrestador(item.source)
            }
        },
        defaultSortField: 'razaoSocial',
        defaultSortOrder: 1
    };
    readonly medicosColumnOrder = ['uf', 'crm', 'nome', 'situacao', 'entrada', 'saida', 'alertas'];
    readonly medicosMetadata: TableMetadata = {
        headers: {
            uf: { type: 'text', default: '-', hidden: false, value: 'UF' },
            crm: { type: 'text', default: '-', hidden: false, value: 'CRM' },
            nome: { type: 'perfil', default: '-', hidden: false, value: 'Nome' },
            situacao: { type: 'text', default: '-', hidden: false, value: 'Situação' },
            entrada: { type: 'text', default: '-', hidden: false, value: 'Entrada' },
            saida: { type: 'text', default: '-', hidden: false, value: 'Saída' },
            alertas: { type: 'tag', default: '-', hidden: false, value: 'Alertas' }
        },
        title: 'nome',
        actions: {
            informacoesFinanceiras: {
                label: 'Informações Financeiras',
                icon: 'pi pi-dollar',
                color: 'success',
                command: (item: MedicoPsmtTabelaRow) => this.abrirInformacoesFinanceirasMedico(item.source)
            }
        },
        defaultSortField: 'crm',
        defaultSortOrder: 1
    };

    private readonly destroyRef = inject(DestroyRef);
    private readonly fotos = inject(FotoMedicoCacheService);
    private readonly prestadorPsmtService = inject(PrestadorPsmtService);
    private readonly financeiroNavigationService = inject(PsmtPrestadorFinanceiroNavigationService);
    private readonly aberturaNavigationService = inject(PrestadorAberturaNavigationService);

    constructor() {
        effect(() => {
            const pessoa = this.pessoa();
            if (!pessoa?.id || !pessoa?.uf) {
                this.limpar();
                return;
            }

            untracked(() => this.carregarPrestadoresClientes());
        });
    }

    onFiltroAlterado(): void {
        this.first.set(0);
        this.carregarPrestadoresClientes();
    }

    onPaginaAlterada(event: PaginatorState): void {
        this.first.set(event.first ?? 0);
        this.rows.set(event.rows ?? 10);
        this.carregarPrestadoresClientes();
    }

    abrirMedicos(prestador: PrestadorPsmtPrestadorClienteResponse): void {
        this.selecionarPrestadorCliente(prestador);
        this.composicaoPsmt = 'ATUAL';
        this.medicosFirst.set(0);
        this.medicosDialogVisivel.set(true);
        this.carregarMedicos();
    }

    selecionarPrestadorCliente(prestador: PrestadorPsmtPrestadorClienteResponse): void {
        this.prestadorClienteSelecionado.set(prestador);
    }

    abrirDetalhes(prestador: PrestadorPsmtPrestadorClienteResponse): void {
        this.prestadorClienteDetalhe.set(prestador);
        this.detalhesDialogVisivel.set(true);
    }

    fecharDetalhes(): void {
        this.detalhesDialogVisivel.set(false);
        this.prestadorClienteDetalhe.set(null);
    }

    fecharMedicos(): void {
        this.medicosDialogVisivel.set(false);
        this.limparMedicos();
    }

    onComposicaoAlterada(): void {
        this.medicosFirst.set(0);
        this.carregarMedicos();
    }

    onPaginaMedicosAlterada(event: PaginatorState): void {
        this.medicosFirst.set(event.first ?? 0);
        this.medicosRows.set(event.rows ?? 10);
        this.carregarMedicos();
    }

    abrirInformacoesFinanceirasPrestador(prestador: PrestadorPsmtPrestadorClienteResponse): void {
        this.financeiroNavigationService.abrirPrestadorCliente(this.pessoa(), prestador);
    }

    abrirPrestador(prestador: PrestadorPsmtPrestadorClienteResponse): void {
        this.aberturaNavigationService.abrirPrestador(this.pessoa(), { id: prestador.idPrestador, uf: prestador.uf, crm: prestador.crm ?? undefined, nome: prestador.razaoSocial ?? undefined, tipo: 'PRESTADOR' }, '/prestadores/psmt');
    }

    abrirInformacoesFinanceirasMedico(medico: PrestadorPsmtMedicoResponse): void {
        this.financeiroNavigationService.abrirMedico(this.pessoa(), medico);
    }

    private carregarPrestadoresClientes(): void {
        const pessoa = this.pessoa();
        if (!pessoa?.id || !pessoa?.uf) {
            this.limpar();
            return;
        }

        this.carregando.set(true);
        this.prestadorPsmtService
            .listarPrestadoresClientes({
                id: Number(pessoa.id),
                uf: pessoa.uf,
                situacaoPsmt: this.filtroPsmt,
                sortBy: 'RAZAO_SOCIAL',
                direction: 'ASC',
                page: Math.floor(this.first() / this.rows()),
                size: this.rows()
            })
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.carregando.set(false))
            )
            .subscribe({
                next: (response) => {
                    this.prestadoresClientes.set(response.content ?? []);
                    this.totalRecords.set(response.totalElements ?? 0);
                },
                // Sem isto, a falha na troca de prestador deixaria os dados do anterior na tela.
                error: () => this.limpar()
            });
    }

    private carregarMedicos(): void {
        const pessoa = this.pessoa();
        const prestadorCliente = this.prestadorClienteSelecionado();
        if (!pessoa?.id || !pessoa?.uf || !prestadorCliente) {
            this.limparMedicos();
            return;
        }

        this.medicosCarregando.set(true);
        this.prestadorPsmtService
            .listarMedicos({
                id: Number(pessoa.id),
                uf: pessoa.uf,
                idPrestadorCliente: prestadorCliente.idPrestador,
                ufPrestadorCliente: prestadorCliente.uf,
                composicao: this.composicaoPsmt,
                sortBy: 'CRM',
                direction: 'ASC',
                page: Math.floor(this.medicosFirst() / this.medicosRows()),
                size: this.medicosRows()
            })
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.medicosCarregando.set(false))
            )
            .subscribe((response) => {
                const medicos = response.content ?? [];
                this.medicos.set(medicos);
                // As linhas são `computed` sobre o cache: cada retrato que chega refaz a sua linha.
                this.fotos.precarregar(medicos.map((medico) => ({ crm: medico.crm, uf: medico.uf })));
                this.medicosTotalRecords.set(response.totalElements ?? 0);
            });
    }

    private limpar(): void {
        this.prestadoresClientes.set([]);
        this.totalRecords.set(0);
        this.first.set(0);
        this.prestadorClienteSelecionado.set(null);
        this.prestadorClienteDetalhe.set(null);
        this.detalhesDialogVisivel.set(false);
        this.limparMedicos();
    }

    private limparMedicos(): void {
        this.medicos.set([]);
        this.medicosTotalRecords.set(0);
        this.medicosFirst.set(0);
    }

    private toPrestadorClienteTabelaRow(prestador: PrestadorPsmtPrestadorClienteResponse): PrestadorClienteTabelaRow {
        return {
            id: `${prestador.uf}-${prestador.idPrestador}`,
            uf: prestador.uf,
            crm: prestador.crm ?? '-',
            razaoSocial: prestador.razaoSocial ?? '-',
            tipoInscricao: prestador.tipoInscricao ?? '-',
            situacao: prestador.situacao ?? '-',
            statusPsmt: prestador.possuiPsmtAtivoNoCorpoClinico ? { value: 'PSMT ativo', color: '#10b981', textColor: '#ffffff' } : { value: 'Sem PSMT ativo', color: '#d946ef', textColor: '#ffffff' },
            source: prestador
        };
    }

    private toMedicoTabelaRow(medico: PrestadorPsmtMedicoResponse): MedicoPsmtTabelaRow {
        return {
            id: `${medico.uf}-${medico.idMedico}`,
            uf: medico.uf,
            crm: medico.crm ?? '-',
            nome: this.fotos.perfilMedico(medico.nome, medico.crm, medico.uf),
            situacao: medico.situacao ?? '-',
            entrada: this.formatarData(medico.entrada),
            saida: this.formatarData(medico.saida),
            alertas: this.alertasMedico(medico),
            source: medico
        };
    }

    formatarData(data?: string | null): string {
        return data ? DateUtils.formatDate(data) : '-';
    }

    private alertasMedico(medico: PrestadorPsmtMedicoResponse): TableTag {
        if (medico.falecido) {
            return { value: 'Falecido', color: '#ef4444', textColor: '#ffffff' };
        }
        if (medico.devedor) {
            return { value: 'Devedor', color: '#f59e0b', textColor: '#111827' };
        }
        return { value: 'Sem alerta', color: '#10b981', textColor: '#ffffff' };
    }

    private certificadoTag(status?: PrestadorPsmtCertificadoRegularidadeStatus | null): TableTag {
        if (status === 'VALIDO') {
            return { value: 'Válido', color: '#10b981', textColor: '#ffffff' };
        }
        if (status === 'EXPIRADO') {
            return { value: 'Expirado', color: '#f59e0b', textColor: '#111827' };
        }
        return { value: 'Sem certificado', color: '#ef4444', textColor: '#ffffff' };
    }
}
